import ValueUtils from '../../utils/ValueUtils';
import ManningContentUtils from '../../utils/ManningContentUtils';
import { IAdditionalProductInfo } from './IAdditionalProductInfo';

export default class ProductInfo {
    id: string;
    externalId: string;
    preTitle?: string;
    title: string;
    subtitle?: string;
    shortSeriesTitle?: string;
    cleanTitle: string;
    cruftlessTitle?: string;
    slug: string;

    authors: string;

    link: string;
    marketplaceLink?: string;
    livebookLink?: string;
    livevideoLink?: string;

    coverPath: string;

    description: string;
    dateDescription: string;
    date?: Date;
    isbn?: string;
    free?: boolean;

    seoKeywords?: string;

    tags: string[];
    tagsText?: string;

    tagsV2: [{
        id: number,
        tagGroups: [number],
        name: string
    }?]

    isLivebook: boolean;
    isLivevideo: boolean;
    isLiveproject?: boolean;
    isLiveprojectSeries?: boolean;
    isLiveaudio?: boolean;
    isConference?: boolean;
    isAuthorTalk?: boolean;
    isSummaryLV?: boolean;
    isProductWalkthrough?: boolean;
    isVideoEdition?: boolean;

    productTypeDescription: string;

    isSpecialProduct?: boolean;
    isBundle?: boolean;
    price?: number;
    priceWithoutDiscount?: number;
    unitAmountDiscounted?: number;
    isEarlierEdition?: boolean;
    isMeap?: boolean;

    skillsLearned?: string;
    authorshipDisplay?: string;
    difficulty?: string;
    durationInWeeks?: number;
    hoursPerWeek?: number

    liveProjects?: any[];
    contrivedPriceBeforeDiscount?: number;

    additionalProductInfo?: IAdditionalProductInfo;

    certifiable?: boolean;
    nextEdition?: number;

    public static convertRawProducts(rawProducts: any, prioritizeLastSignificantDate: boolean = false): ProductInfo[] {
        const titleBooksMap: {[title: string]: ProductInfo[]} = {};
        let results;

        //First, usual case where it is an array of products
        if(rawProducts.length > 0){
            results = rawProducts.map(rawProduct => {
                const relevantDateString = prioritizeLastSignificantDate && !rawProduct.livebook
                                         ? (rawProduct.lastSignificantDate || rawProduct.publishedDate || rawProduct.expectedPublishDate)
                                         : (rawProduct.publishedDate || rawProduct.lastSignificantDate || rawProduct.expectedPublishDate);
                
                const date = relevantDateString ? new Date(relevantDateString): null;
                let productTypeDescription = "";

                if(rawProduct.livebook){
                    productTypeDescription = "livebook";
                }
                else if(rawProduct.isConference) {
                    productTypeDescription = "conference";
                }
                else if(rawProduct.isAuthorTalk) {
                    productTypeDescription = "author talk";
                }
                else if(rawProduct.isSummaryLV) {
                    productTypeDescription = "summary video";
                }
                else if(rawProduct.isProductWalkthrough) {
                    productTypeDescription = "liveProject walkthrough";
                }
                else if(rawProduct.isVideoEdition) {
                    productTypeDescription = "video edition";
                }
                else if(rawProduct.isLiveVideo){
                    productTypeDescription = "livevideo";
                }
                else if(rawProduct.isLiveProject){
                    productTypeDescription = "liveproject";
                }
                else if((rawProduct.authors || rawProduct.authorshipDisplay || "").indexOf("chapters selected") != -1){
                    productTypeDescription = "free";
                }

                const tags = rawProduct.tags != null && rawProduct.tags.length ? rawProduct.tags.map(it => it.name ? it.name : it) : [];
                const tagsV2 = rawProduct.tagsV2 || [];

                let externalId = rawProduct.externalId;

                if(!!rawProduct.isBundle && !externalId){
                    externalId = "bundle-" + rawProduct.id;
                }

                const result = {
                    id: rawProduct.id,
                    externalId: externalId,
                    preTitle: rawProduct.preTitle,
                    title: rawProduct.title,
                    subtitle: rawProduct.subtitle,
                    cleanTitle: ManningContentUtils.cleanBookTitle(rawProduct.title),
                    cruftlessTitle: ManningContentUtils.removeCruftFromTitle(ManningContentUtils.cleanBookTitle(rawProduct.title)),
                    slug: rawProduct.slug,
                    isbn: rawProduct.isbn,
                    skillsLearned: rawProduct.skillsLearned,
                    authorshipDisplay: rawProduct.authorshipDisplay,
                    difficulty: rawProduct.difficulty,
                    durationInWeeks: rawProduct.durationInWeeks,
                    hoursPerWeek: rawProduct.hoursPerWeek,

                    free: rawProduct.free,
                    certifiable: rawProduct.certifiable,

                    authors: rawProduct.authors || rawProduct.authorshipDisplay,
                    link: "",
                    coverPath: rawProduct.coverPath || rawProduct.imageUrl,

                    description: (rawProduct.subtitle || "") + " " + (rawProduct.aboutProductDescription || rawProduct.description || ""),
                    dateDescription: ValueUtils.getDateAsString(date),
                    date: date,
                    tags: tags,
                    tagsText: tags.join(", "),
                    tagsV2: tagsV2,
                    seoKeywords: rawProduct.seoKeywords || "",

                    isLivebook: !!rawProduct.livebook,
                    isLivevideo: !!rawProduct.isLiveVideo,
                    isLiveproject: !!rawProduct.isLiveproject || !!rawProduct.isLiveProject,
                    isLiveprojectSeries: (!rawProduct.isLiveProjectSeries || !!rawProduct.isLiveproject || !!rawProduct.isLiveProject) && this.hasLPSeriesOffering(rawProduct),
                    productTypeDescription: productTypeDescription,
                    isLiveaudio: !!rawProduct.hasAudio || !!rawProduct.isLiveaudio,
                    isBundle: !!rawProduct.isBundle,
                    isConference: !!rawProduct.isConference || !!rawProduct.conference,
                    isAuthorTalk: !!rawProduct.isAuthorTalk  || !!rawProduct.authorTalk,
                    isVideoEdition: !!rawProduct.isVideoEdition  || !!rawProduct.videoEdition,
                    isSummaryLV: !!rawProduct.isSummaryLV  || !!rawProduct.summaryLV,
                    isProductWalkthrough: !!rawProduct.isProductWalkthrough  || !!rawProduct.productWalkthrough,
                    price: this.getRawProductPrice(rawProduct),
                    priceWithoutDiscount: this.getRawProductPriceWithoutDiscount(rawProduct),
                    unitAmountDiscounted: this.getRawProductUnitAmountDiscounted(rawProduct),
                    isMeap: rawProduct.isMeap
                };

                let titleKey = `${result.cleanTitle}-${result.isLivevideo ? 1: 0}-${result.isLiveproject ? 1: 0}-${result.isLiveprojectSeries ? 1: 0}`;

                if(!titleBooksMap[titleKey]) {
                    titleBooksMap[titleKey] = [];
                }

                titleBooksMap[titleKey].push(result);

                if(rawProduct.isBundle){
                    (result as any).additionalProductInfo = {
                        id: result.id,
                        externalId: result.externalId,
                        description: result.description,
                        cleanDescription: result.description,
                        rating: undefined,
                        productOfferings: rawProduct.productOfferings,
                        price: this.getRawProductPrice(rawProduct),
                        priceWithoutDiscount: this.getRawProductPriceWithoutDiscount(rawProduct),
                        unitAmountDiscounted: this.getRawProductUnitAmountDiscounted(rawProduct),
                    };
                }

                return result;
            });
        }
        //now liveProject version that has liveProjects and liveProjectSeries
        else if(rawProducts.liveProjects?.length || rawProducts.liveProjectSeries?.length){
            let liveProjects = rawProducts.liveProjects?.map(rawProduct => {
                const relevantDateString = prioritizeLastSignificantDate
                                         ? (rawProduct.lastSignificantDate || rawProduct.publishedDate || rawProduct.expectedPublishDate)
                                         : (rawProduct.publishedDate || rawProduct.expectedPublishDate || rawProduct.lastSignificantDate);

                const date = relevantDateString ? new Date(relevantDateString): null
                let productTypeDescription = "";

                productTypeDescription = "liveproject";

                const tags = rawProduct.tags != null && rawProduct.tags.length ? rawProduct.tags.map(it => it.name ? it.name : it) : [];
                const tagsV2 = rawProduct.tagsV2 || [];

                let externalId = rawProduct.externalId;

                if(!!rawProduct.isBundle && !externalId){
                    externalId = "bundle-" + rawProduct.id;
                }

                const result = {
                    id: rawProduct.id,
                    externalId: externalId,
                    preTitle: rawProduct.preTitle,
                    title: rawProduct.title,
                    subtitle: rawProduct.subtitle,
                    shortSeriesTitle: rawProduct.shortSeriesTitle,
                    cleanTitle: ManningContentUtils.cleanBookTitle(rawProduct.title),
                    cruftlessTitle: ManningContentUtils.removeCruftFromTitle(ManningContentUtils.cleanBookTitle(rawProduct.title)),
                    slug: rawProduct.slug,
                    isbn: rawProduct.isbn,
                    skillsLearned: rawProduct.skillsLearned,
                    authorshipDisplay: rawProduct.authorshipDisplay,
                    difficulty: rawProduct.difficulty,
                    durationInWeeks: rawProduct.durationInWeeks,
                    hoursPerWeek: rawProduct.hoursPerWeek,
                
                    free: rawProduct.free,
                    certifiable: rawProduct.certifiable,

                    authors: rawProduct.authors || rawProduct.authorshipDisplay,
                    link: "",
                    coverPath: rawProduct.coverPath || rawProduct.imageUrl,

                    description: (rawProduct.subtitle || "") + " " + (rawProduct.aboutProductDescription || rawProduct.description || ""),
                    dateDescription: ValueUtils.getDateAsString(date),
                    date: date,
                    tags: tags,
                    tagsText: tags.join(", "),
                    tagsV2: tagsV2,
                    seoKeywords: rawProduct.seoKeywords || "",

                    isLivebook: !!rawProduct.livebook,
                    isLivevideo: !!rawProduct.isLiveVideo,
                    isLiveproject: true,
                    isLiveprojectSeries: false,
                    productTypeDescription: productTypeDescription,
                    isLiveaudio: !!rawProduct.hasAudio || !!rawProduct.isLiveaudio,
                    isBundle: !!rawProduct.isBundle,
                    isConference: !!rawProduct.isConference || !!rawProduct.conference,
                    isAuthorTalk: !!rawProduct.isAuthorTalk  || !!rawProduct.authorTalk,
                    isVideoEdition: !!rawProduct.isVideoEdition  || !!rawProduct.videoEdition,
                    isSummaryLV: !!rawProduct.isSummaryLV  || !!rawProduct.summaryLV,
                    isProductWalkthrough: !!rawProduct.isProductWalkthrough  || !!rawProduct.productWalkthrough,
                    price: this.getRawProductPrice(rawProduct),
                    priceWithoutDiscount: this.getRawProductPriceWithoutDiscount(rawProduct),
                    unitAmountDiscounted: this.getRawProductUnitAmountDiscounted(rawProduct),
                    isMeap: rawProduct.isMeap
                };

                let titleKey = `${result.cleanTitle}-${result.isLivevideo ? 1: 0}-${result.isLiveproject ? 1: 0}-${result.isLiveprojectSeries ? 1: 0}`;

                if(!titleBooksMap[titleKey]) {
                    titleBooksMap[titleKey] = [];
                }

                titleBooksMap[titleKey].push(result);

                if(rawProduct.isBundle){
                    (result as any).additionalProductInfo = {
                        id: result.id,
                        externalId: result.externalId,
                        description: result.description,
                        cleanDescription: result.description,
                        rating: undefined,
                        productOfferings: rawProduct.productOfferings,
                        price: this.getRawProductPrice(rawProduct),
                        priceWithoutDiscount: this.getRawProductPriceWithoutDiscount(rawProduct),
                        unitAmountDiscounted: this.getRawProductUnitAmountDiscounted(rawProduct),
                    };
                }

                return result;
            });

            let liveProjectSeries = rawProducts.liveProjectSeries?.map(rawProduct => {
                const relevantDateString = prioritizeLastSignificantDate
                                         ? (rawProduct.lastSignificantDate || rawProduct.publishedDate || rawProduct.expectedPublishDate)
                                         : (rawProduct.publishedDate || rawProduct.expectedPublishDate || rawProduct.lastSignificantDate);

                const date = relevantDateString ? new Date(relevantDateString): null
                let productTypeDescription = "";

                productTypeDescription = "liveproject";

                const tags = rawProduct.tags != null && rawProduct.tags.length ? rawProduct.tags.map(it => it.name ? it.name : it) : [];
                const tagsV2 = rawProduct.tagsV2 || [];
                let externalId = "bundle-" + rawProduct.id;

                const result = {
                    id: rawProduct.id,
                    externalId: externalId,
                    preTitle: rawProduct.preTitle,
                    title: rawProduct.title,
                    subtitle: rawProduct.subtitle,
                    cleanTitle: ManningContentUtils.cleanBookTitle(rawProduct.title),
                    cruftlessTitle: ManningContentUtils.removeCruftFromTitle(ManningContentUtils.cleanBookTitle(rawProduct.title)),
                    slug: rawProduct.slug,
                    isbn: rawProduct.isbn,
                    skillsLearned: rawProduct.skillsLearned,
                    authorshipDisplay: rawProduct.authorshipDisplay,
                    difficulty: rawProduct.difficulty,
                    durationInWeeks: rawProduct.durationInWeeks,
                    hoursPerWeek: rawProduct.hoursPerWeek,

                    liveProjects: rawProduct.liveProjects,
                    contrivedPriceBeforeDiscount: rawProduct.contrivedPriceBeforeDiscount,

                    free: rawProduct.free,
                    certifiable: rawProduct.certifiable,
                  
                    authors: rawProduct.authors || rawProduct.authorshipDisplay,
                    link: rawProduct.link_to_marketplace || "",
                    coverPath: rawProduct.coverPath || rawProduct.imageUrl,

                    description: (rawProduct.subtitle || "") + " " + (rawProduct.aboutProductDescription || rawProduct.description || ""),
                    dateDescription: ValueUtils.getDateAsString(date),
                    date: date,
                    tags: tags,
                    tagsText: tags.join(", "),
                    tagsV2: tagsV2,
                    seoKeywords: rawProduct.seoKeywords || "",

                    isLivebook: !!rawProduct.livebook,
                    isLivevideo: !!rawProduct.isLiveVideo,
                    isLiveproject: true,
                    isLiveprojectSeries: true,
                    productTypeDescription: productTypeDescription,
                    isLiveaudio: !!rawProduct.hasAudio || !!rawProduct.isLiveaudio,
                    isBundle: !!rawProduct.isBundle,
                    isConference: !!rawProduct.isConference || !!rawProduct.conference,
                    isAuthorTalk: !!rawProduct.isAuthorTalk  || !!rawProduct.authorTalk,
                    isVideoEdition: !!rawProduct.isVideoEdition  || !!rawProduct.videoEdition,
                    isSummaryLV: !!rawProduct.isSummaryLV  || !!rawProduct.summaryLV,
                    isProductWalkthrough: !!rawProduct.isProductWalkthrough  || !!rawProduct.productWalkthrough,
                    price: this.getRawProductPrice(rawProduct),
                    priceWithoutDiscount: rawProduct.priceWithoutDiscount || this.getRawProductPriceWithoutDiscount(rawProduct),
                    unitAmountDiscounted: this.getRawProductUnitAmountDiscounted(rawProduct),
                    isMeap: rawProduct.isMeap
                };

                if(!titleBooksMap[result.cleanTitle]) {
                    titleBooksMap[result.cleanTitle] = [];
                }

                titleBooksMap[result.cleanTitle].push(result);

                (result as any).additionalProductInfo = {
                    id: result.id,
                    externalId: result.externalId,
                    description: result.description,
                    cleanDescription: result.description,
                    rating: undefined,
                    productOfferings: rawProduct.productOfferings,
                    priceWithoutDiscount: this.getRawProductPriceWithoutDiscount(rawProduct),
                    price: this.getRawProductPrice(rawProduct),
                    unitAmountDiscounted: this.getRawProductUnitAmountDiscounted(rawProduct)
                };

                return result;
            });

            results = [...liveProjects, ...liveProjectSeries];
        }

        for(const titleKey in titleBooksMap){
            if(titleBooksMap[titleKey].length > 1){
                const books = titleBooksMap[titleKey];
                let newestBook = books[0];
                newestBook.isEarlierEdition = false;

                for(let i = 1; i < books.length; i++){
                    const currentBook = books[i];

                    if(currentBook.isBundle) { continue; }

                    if(currentBook.date > newestBook.date){
                        newestBook.isEarlierEdition = true;
                        newestBook = currentBook;
                    }
                    else {
                        currentBook.isEarlierEdition = true;
                    }
                }
            }
        }

        return results;
    }

    private static hasLPSeriesOffering(rawProduct): boolean {
        if(!rawProduct || !rawProduct.productOfferings || !rawProduct.productOfferings) { return false; }

        const offering = rawProduct.productOfferings.find(it => it.productType == "LP_SERIES");

        return offering != null;
    }

    private static getRawProductPrice(rawProduct): number {
        if(rawProduct.isBundle){
            return rawProduct.price;
        }
        else if(rawProduct.isLiveVideo){
            return this.getVideoPrice(rawProduct);
        }
        else if(rawProduct.isLiveProject){
            return this.getLiveProjectPrice(rawProduct);
        }
        else if(rawProduct.liveProjects?.length){
            return this.getLiveProjectSeriesPrice(rawProduct);
        }
        else {
            return this.getEbookPrice(rawProduct)
        }
    }

    private static getRawProductUnitAmountDiscounted(rawProduct): number {
        if(rawProduct.isBundle){
          const unitAmountDiscounted = rawProduct.unitAmountDiscounted != null ? rawProduct.unitAmountDiscounted : 0;
          return unitAmountDiscounted;
        }
        if(!rawProduct.isBundle){
          const unitAmountDiscounted = rawProduct.unitAmountDiscounted != null ? rawProduct.unitAmountDiscounted : 0;
          return unitAmountDiscounted;
        }
        else if(rawProduct.isLiveVideo){
            return this.getVideoPrice(rawProduct);
        }
        else if(rawProduct.isLiveProject){
            return this.getLiveProjectPrice(rawProduct);
        }
        else if(rawProduct.liveProjects?.length){
            return this.getLiveProjectSeriesUnitAmountDiscountedPrice(rawProduct);
        }
        else {
            return this.getEbookPrice(rawProduct)
        }
    }

    private static getRawProductPriceWithoutDiscount(rawProduct): number {
        return this.getRawProductPrice(rawProduct);
    }

    private static getEbookPrice(rawProduct){
        if(!rawProduct || !rawProduct.productOfferings || !rawProduct.productOfferings) { return 0; }

        const ebookOffering = rawProduct.productOfferings.find(it => it.productType == "EBOOK");

        return ebookOffering != null ? ebookOffering.price : 0;
    }

    private static getLiveProjectPrice(rawProduct){
        if(!rawProduct || !rawProduct.productOfferings || !rawProduct.productOfferings) { return 0; }

        const offering = rawProduct.productOfferings.find(it => it.productType == "SOLO" || it.productType == "LP_SERIES");

        return offering != null ? offering.price : 0;
    }

    private static getLiveProjectSeriesPrice(rawProduct){
        if(!rawProduct || !rawProduct.price) { return 0; }

        return rawProduct.price;
    }

    private static getLiveProjectSeriesUnitAmountDiscountedPrice(rawProduct){
        if(!rawProduct || !rawProduct.unitAmountDiscounted) { return 0; }

        return rawProduct.unitAmountDiscounted;
    }

    private static getVideoPrice(rawProduct){
        if(!rawProduct || !rawProduct.productOfferings || !rawProduct.productOfferings) { return 0; }

        const videoOffering = rawProduct.productOfferings.find(it => it.productType == "LIVEVIDEO");

        return videoOffering != null ? videoOffering.price : 0;
    }

    public static createSpecialMeapProduct(){
        return this.createSpecialProduct(
            "MEAP",
            "Manning Publications",
            "A book can take a year or more to write, so how do you learn that hot new technology today? The answer is MEAP, the Manning Early Access Program. In MEAP, you read a book chapter-by-chapter while it's being written and get the final eBook as soon as it's finished. If you pre-order the pBook, you'll get it long before it's available in stores.",
            this.getManningBaseUrl() + "/meap-catalog",
            this.getManningBaseUrl() + "/meap-catalog"
        );
    }

    public static createSpecialLiveprojectProduct(){
        return this.createSpecialProduct(
            "liveProject",
            "Manning Publications",
            "In liveProject you complete a realistic project organized in achievable steps using carefully-selected book and video resources. You have the option to collaborate with other participants and a mentor. You learn by doing.",
            "https://liveproject.manning.com",
            "https://liveproject.manning.com"
        );
    }

    public static createSpecialLivevideoProduct(){
        return this.createSpecialProduct(
            "liveVideo",
            "Manning Publications",
            "Manning video courses are crafted to serve you as excellent learning tools. They link to the video from its text, they help you learn with exercises, and they support executable code for many languages.",
            "https://livevideo.manning.com",
            "https://www.manning.com/livevideo-program"
        );
    }

    public static createSpecialLivebookProduct(){
        return this.createSpecialProduct(
            "liveBook",
            "Manning Publications",
            "liveBooks are enhanced books. They add narration, interactive exercises, code execution, and other features to eBooks.",
            "https://livebook.manning.com",
            "https://www.manning.com/livebook-program#section-what-is-livebook"
        );
    }

    public static createSpecialManningSiteProduct(){
        return this.createSpecialProduct(
            "Manning Home Site",
            "Manning Publications",
            "Manning is an independent publisher of computer books and video courses for software developers, engineers, architects, system administrators, managers and all who are professionally involved with the computer business. We also publish for students and young programmers, including occasionally for children. We are an entirely virtual organization based on Shelter Island, New York, with many staff working from far-flung places like Manila and Zagreb.",
            "https://www.manning.com/",
            "https://www.manning.com/",
            false
        );
    }

    public static createSpecialForumProduct(){
        return this.createSpecialProduct(
            "Forum",
            "Manning Publications",
            "The Manning forums are a place for readers and technologists to engage in discussions about our books and the subjects they cover.",
            "https://forums.manning.com/forums/list.page",
            "https://forums.manning.com/forums/list.page",
            false
        );
    }

    public static createSpecialSupportProduct(){
        return this.createSpecialProduct(
            "Support",
            "Manning Publications",
            "Do you have a question or problem with the site? Need help? The Manning FAQs provides answers to frequently asked questions. And if all else fails, you can always contact Manning support and we'll be happy to respond.",
            this.getManningBaseUrl() + "/support",
            this.getManningBaseUrl() + "/support",
            false
        );
    }

    public static createSpecialNewsletterProduct(){
        return this.createSpecialProduct(
            "Newsletter",
            "Manning Publications",
            "Subscribe to our newsletter",
            this.getManningBaseUrl() + "/mail-preferences",
            this.getManningBaseUrl() + "/mail-preferences",
            false
        );
    }

    public static createSpecialContactProduct(){
        return this.createSpecialProduct(
            "Contact",
            "Manning Publications",
            "Contact us: Manning Publications Co. 20 Baldwin Road; PO Box 761; Shelter Island, NY  11964",
            this.getManningBaseUrl() + "/contact",
            this.getManningBaseUrl() + "/contact",
            false
        );
    }

    public static createSpecialFreeContentProduct(){
        return this.createSpecialProduct(
            "Free content center",
            "Manning Publications",
            "Manning Free Content Center",
            "http://freecontent.manning.com/",
            "http://freecontent.manning.com/",
            false
        );
    }

    public static createSpecialCatalogProduct(){
        return this.createSpecialProduct(
            "Catalog",
            "Manning Publications",
            "The catalog shows all our books and videos in one place",
            this.getManningBaseUrl() + "/catalog",
            this.getManningBaseUrl() + "/catalog",
            false
        );
    }

    public static createSpecialDealOfTheDayProduct(){
        return this.createSpecialProduct(
            "Deal of the Day",
            "Manning Publications",
            "Deal of the Day",
            this.getManningBaseUrl() + "/dotd",
            this.getManningBaseUrl() + "/dotd",
            false
        );
    }

    public static createSpecialTokenProduct(){
        return this.createSpecialProduct(
            "tokens",
            "Use tokens to unlock pieces of book content.",
            "Use tokens to unlock pieces of book content.",
            this.getManningBaseUrl() + "/token",
            this.getManningBaseUrl() + "/token"
        );
    }

    public static createSpecialProduct(title: string, authors:string, description: string, link: string, marketplaceLink: string, useTitleAsImage: boolean = true){
        const productInfo = new ProductInfo();

        productInfo.title = title;
        productInfo.authors = authors;
        productInfo.coverPath = useTitleAsImage ? `https://lx-common-resources.s3-us-west-2.amazonaws.com/resources/${title}.jpg` : "";
        productInfo.description = description;
        productInfo.externalId = title;
        productInfo.link = link;
        productInfo.marketplaceLink = marketplaceLink;
        productInfo.slug = "";
        productInfo.tags = [];
        productInfo.tagsV2 = [];
        productInfo.date = new Date();
        productInfo.dateDescription = ValueUtils.getDateAsString(new Date());
        productInfo.isSpecialProduct = true;

        return productInfo;
    }

    private static getManningBaseUrl(){
        try {
            const currentLocation = document.location.href;
            if(currentLocation.indexOf("www-qa.") >= 0
            || currentLocation.indexOf("livebook-qa.") >= 0
            || currentLocation.indexOf("livevideo-qa.") >= 0){
                return "https://www-qa.manning.com";
            }
        }
        catch(e){}
        return "https://www.manning.com";
    }
}